import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  HeaderSection,
  MainSection,
  SubHeaderSection,
  QuizAnswer,
  QuizInnerLayout,
  QuizFooterContainer,
  QuizFooterInnerContainer,
  QuizHeader,
  QuizTimerContainer,
  QuizChoiceLayoutWithSpace as BaseQuizChoiceLayoutWithSpace,
} from '@skooldio/skooldio-classroom-components';

import QuizWrapper from 'Components/Quiz/Components/QuizWrapper';
import Loading from 'Components/Loading';
import { Box, ButtonOutlined, ButtonFilled, Flex, Text } from 'Components/Base';
import { useConfig } from 'Context/ConfigContext';
import { getTextColorByContrast } from 'Util/color';
import { EXAM_PAPER_ITEM_TYPE } from 'Components/Quiz/constant';
import { useDisclosure } from 'Util/Hooks';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import QuizQuestion from 'Components/Quiz/Components/QuizQuestion';

import PassagePanel from './PassagePanel';

const QuestionWrapper = styled(Box)`
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.greyscale.grey5}`};
  p:first-child {
    margin-top: 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

const QuizChoiceLayoutWithSpace = styled(BaseQuizChoiceLayoutWithSpace)`
  & img {
    max-width: 100%;
  }
`;

/**
 * LatexWrapper fixes a MathJax rendering issue in Electron, where mjx-root elements
 * sometimes have a width of 0px, causing LaTeX content to be hidden. This forces
 * a minimal width to ensure proper rendering.
 */
const LatexWrapper = styled(Box)`
  .MathJax mjx-root {
    width: 1px !important; // Ensure LaTeX renders correctly in Electron
  }
`;

const AnswerLabel = styled(({ isFirstIndex, hasSelectedAnswer, isMobile, ...restProps }) => (
  <Box
    style={{
      width: hasSelectedAnswer ? (isMobile ? 'auto' : '148px') : '100%',
      marginLeft: !isFirstIndex && hasSelectedAnswer ? (isMobile ? '0px' : '16px') : '0px',
      marginTop: !isFirstIndex && hasSelectedAnswer ? (isMobile ? '16px' : '0px') : '0px',
      padding: '16px 24px',
    }}
    {...restProps}
  />
))`
  background: ${({ theme }) => theme.colors.notiGrey.grey3};
  border: 1px solid ${({ theme }) => theme.colors.snackbarBackground};
`;

const notSelectAnswerLabel = 'ไม่ได้เลือกคำตอบ';

const renderReviewAnswer = (question, latexTranspiler, isMobile) => {
  const { choices, type, selectedAnswer } = question || {};
  let quizReviewAnswer = [];

  switch (type) {
    case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE:
    case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE_DIFFERENT_SCORE: {
      const label = selectedAnswer
        ? choices.find((c) => c.value === selectedAnswer).label
        : notSelectAnswerLabel;
      /**
       * Wrap the label with LatexWrapper to ensure proper LaTeX rendering.
       * This workaround is needed for cases where the first character isn't a
       * simple letter or number, which can cause rendering issues in Electron.
       */
      const quizAnswer = (
        <LatexWrapper>
          <QuizAnswer label={label} variant={latexTranspiler} />
        </LatexWrapper>
      );
      quizReviewAnswer.push(quizAnswer);
      break;
    }
    case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT:
    case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT_WITH_CORRECT_ANSWERS: {
      if (selectedAnswer) {
        selectedAnswer.sort();
        for (let i = 0; i < selectedAnswer.length; i++) {
          const label = choices.find((c) => c.value === selectedAnswer[i]).label;
          // Wrap the label with a div and insert an div with space in front.
          // This workaround is necessary for correct LaTeX rendering when the
          // first character of the label is not a simple character (like a number or letter)
          const formattedLabel = `<div style="display: flex;"><div>&nbsp;</div><div>${label}</div></div>`;
          const quizAnswer = <QuizAnswer label={formattedLabel} variant={latexTranspiler} />;
          quizReviewAnswer.push(quizAnswer);
        }
      } else {
        const label = notSelectAnswerLabel;
        const quizAnswer = <QuizAnswer label={label} variant={latexTranspiler} />;
        quizReviewAnswer.push(quizAnswer);
      }
      break;
    }
    case EXAM_PAPER_ITEM_TYPE.FILL_NUMBER: {
      const label = selectedAnswer && selectedAnswer !== '' ? selectedAnswer : 'ไม่ได้ตอบ';
      const quizAnswer = <QuizAnswer label={label} variant={latexTranspiler} />;
      quizReviewAnswer.push(quizAnswer);
      break;
    }
    case EXAM_PAPER_ITEM_TYPE.CONNECTION: {
      const isSelectedAnswer = !!selectedAnswer;
      const isAllAnswersEmpty =
        isSelectedAnswer && selectedAnswer.every((row) => row.every((cell) => cell === ''));
      const hasSelectedAnswer = isSelectedAnswer && !isAllAnswersEmpty;

      if (hasSelectedAnswer) {
        selectedAnswer.forEach((row, index) => {
          const label = row.join('') || 'ไม่มี';
          const quizAnswer = (
            <AnswerLabel
              key={`quiz-answer-${index}`}
              data-testid={`quiz-answer-${index}`}
              isMobile={isMobile}
              hasSelectedAnswer={hasSelectedAnswer}
              isFirstIndex={index === 0}
            >
              {label}
            </AnswerLabel>
          );
          quizReviewAnswer.push(quizAnswer);
        });
      } else {
        const label = notSelectAnswerLabel;
        const quizAnswer = (
          <AnswerLabel data-testid="quiz-answer" hasSelectedAnswer={hasSelectedAnswer}>
            {label}
          </AnswerLabel>
        );
        quizReviewAnswer.push(quizAnswer);
      }
      break;
    }
    default: {
      const label = selectedAnswer
        ? choices.find((c) => c.value === selectedAnswer).label
        : notSelectAnswerLabel;
      const quizAnswer = <QuizAnswer label={label} variant={latexTranspiler} />;
      quizReviewAnswer.push(quizAnswer);
      break;
    }
  }

  return quizReviewAnswer;
};

function ViewPassageButton({ children, ...restProps }) {
  return (
    <ButtonFilled
      color={'clear.clearGrayLight'}
      style={{ marginTop: 16, padding: '8px 16px', width: 100, alignSelf: 'flex-end' }}
      {...restProps}
    >
      {children}
    </ButtonFilled>
  );
}

function ConnectionReviewAnswer({ children, isMobile, ...restProps }) {
  return (
    <Flex flexDirection={isMobile ? 'column' : 'row'} {...restProps}>
      {children}
    </Flex>
  );
}

const ReviewAnswer = ({
  onEndQuiz,
  questions,
  onBack,
  onSubmitSession,
  timeRemaining,
  isTimeout,
  isSubmitting,
  layoutType,
}) => {
  const {
    classroom: { quiz = {} },
  } = useConfig();
  const { bgColor = 'primary' } = quiz;
  const { colors } = useTheme();
  const latexTranspiler = quiz?.latexTranspiler;
  const dataTestIdPrefix = 'btn-quiz';

  const { isMobile } = useCompatibleDisplay();
  const [isOpen, onOpen, onClose] = useDisclosure();
  const [passage, setPassage] = useState({ title: '', questionNo: '' });

  const handleClosePassagePanel = () => {
    setPassage({ title: '', questionNo: '' });
    onClose();
  };

  return (
    <QuizWrapper subHeader={timeRemaining ? true : false}>
      <HeaderSection>
        <QuizHeader
          title={'คุณต้องการส่งคำตอบ'}
          onClose={onEndQuiz}
          outerWrapperProps={{ bg: colors[bgColor] }}
        />
      </HeaderSection>
      {timeRemaining && (
        <SubHeaderSection>
          <QuizTimerContainer
            color={isTimeout ? colors.danger : colors.primary}
            isTimeout={isTimeout}
            timeRemaining={timeRemaining}
          />
        </SubHeaderSection>
      )}
      <MainSection>
        <QuizInnerLayout fullWidth>
          <Box mx={'auto'}>
            {questions.map((q, index) => {
              const { type, content, passage } = q || {};
              let quizReviewAnswer = renderReviewAnswer(q, latexTranspiler, isMobile);

              const QuizReviewAnswer =
                type === EXAM_PAPER_ITEM_TYPE.CONNECTION
                  ? ConnectionReviewAnswer
                  : QuizChoiceLayoutWithSpace;

              return (
                <QuestionWrapper key={q.id} pt={5} pb={6} index={index}>
                  <Flex
                    flexDirection={'column'}
                    justifyContent={'center'}
                    maxWidth={1164}
                    px={6}
                    m={'auto'}
                  >
                    <QuizQuestion
                      question={content}
                      questionNo={`${index + 1}.`}
                      variant={latexTranspiler}
                    />
                    <QuizReviewAnswer isMobile={isMobile}>{quizReviewAnswer}</QuizReviewAnswer>
                    {passage && (
                      <ViewPassageButton
                        onClick={() => {
                          setPassage({ title: passage, questionNo: `${index + 1}.` });
                          onOpen();
                        }}
                      >
                        <Text color="text" textStyle="body1" fontWeight={700}>
                          {'ดูบทความ'}
                        </Text>
                      </ViewPassageButton>
                    )}
                  </Flex>
                </QuestionWrapper>
              );
            })}
          </Box>
          <QuizFooterContainer>
            <QuizFooterInnerContainer>
              <ButtonOutlined
                data-testid={`${dataTestIdPrefix}-review-again`}
                height={40}
                borderRadius={20}
                onClick={onBack}
                disabled={isTimeout}
                fontWeight={600}
                py={0}
              >
                ทบทวนอีกครั้ง
              </ButtonOutlined>
              <ButtonFilled
                data-testid={`${dataTestIdPrefix}-review-answer-submit`}
                onClick={onSubmitSession}
                disabled={isSubmitting}
                height={40}
                borderRadius={20}
                textColor={getTextColorByContrast(colors.primary, 'text', 'white')}
                fontWeight={600}
                py={0}
              >
                {isSubmitting ? <Loading width={24} height={24} /> : 'ส่งคำตอบ'}
              </ButtonFilled>
              <PassagePanel
                openDrawer={isOpen}
                isHorizontalDrawer={!isMobile}
                onCloseDrawer={handleClosePassagePanel}
                passage={passage}
                layoutType={layoutType}
              />
            </QuizFooterInnerContainer>
          </QuizFooterContainer>
        </QuizInnerLayout>
      </MainSection>
    </QuizWrapper>
  );
};

ReviewAnswer.defaultProps = {
  questions: [],
  onEndQuiz: () => {},
  onBack: () => {},
  onSubmitSession: () => {},
};

export default ReviewAnswer;
